import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Paper,
  Divider,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';
import { colors, shadows } from '../styles/styleConstants';
import QRScanner from './QRScanner';

/**
 * QRLabelPrint Component
 * 
 * Combined QR code management dialog:
 * - Tab 1: Print QR Label
 * - Tab 2: Manage QR Code (generate new, scan existing)
 * 
 * Props:
 *   open: boolean - Whether the dialog is open
 *   onClose: function - Callback when dialog closes
 *   boxId: number - The ID of the box
 *   boxName: string - The name of the box (for display on label)
 *   currentUuid: string - Current UUID assigned to the box (optional)
 *   onQrUpdate: function - Callback when QR code is updated (optional)
 */
const QRLabelPrint = ({ open, onClose, boxId, boxName, currentUuid, onQrUpdate }) => {
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [manageStep, setManageStep] = useState('choose'); // 'choose', 'scan', 'confirm', 'success'
  const [scannedUuid, setScannedUuid] = useState(null);
  const [manualUuid, setManualUuid] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const printRef = useRef(null);
  const printIframeRef = useRef(null);

  useEffect(() => {
    if (open && boxId) {
      fetchQRCode();
      // Reset manage tab state
      setManageStep('choose');
      setScannedUuid(null);
      setManualUuid('');
      setShowManualEntry(false);
      setSuccessMessage('');
      setError(null);
      // If no QR code assigned, default to Manage tab
      if (!currentUuid) {
        setActiveTab(1);
      } else {
        setActiveTab(0);
      }
    }
  }, [open, boxId, currentUuid]);

  const fetchQRCode = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/boxes/${boxId}/qr-code?format=base64&size=300`);
      setQrCodeData(response.data);
    } catch (err) {
      console.error('Error fetching QR code:', err);
      // Don't show error for missing QR - just means none assigned yet
      if (err.response?.status !== 404) {
        setError('Failed to generate QR code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!qrCodeData?.qr_code) return;

    const qrImageSrc = `data:image/png;base64,${qrCodeData.qr_code}`;
    const labelBoxName = boxName || 'Unnamed Box';

    // Remove existing iframe if any
    if (printIframeRef.current) {
      document.body.removeChild(printIframeRef.current);
    }

    // Create hidden iframe for printing (works better on mobile than window.open)
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    printIframeRef.current = iframe;
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!iframeDoc) {
      alert('Unable to create print preview. Please try again.');
      return;
    }

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Label - ${labelBoxName}</title>
          <style>
            @page { size: auto; margin: 10mm; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; padding: 20px; box-sizing: border-box; background: white; }
            .label-container { text-align: center; padding: 20px; border: 2px dashed #ccc; border-radius: 12px; max-width: 350px; background: white; }
            .qr-code { width: 200px; height: 200px; margin: 0 auto 16px; display: block; }
            .box-name { font-size: 18px; font-weight: bold; color: #333; margin: 0; word-wrap: break-word; }
            .scan-text { font-size: 12px; color: #666; margin-top: 8px; }
            .logo-text { font-size: 10px; color: #999; margin-top: 12px; }
            @media print { 
              body { min-height: auto; background: white; } 
              .label-container { border: 2px dashed #ccc; }
            }
          </style>
        </head>
        <body>
          <div class="label-container">
            <img id="qr-image" class="qr-code" src="${qrImageSrc}" alt="QR Code" />
            <p class="box-name">${labelBoxName}</p>
            <p class="scan-text">Scan to view contents</p>
            <p class="logo-text">ClutterLogix</p>
          </div>
        </body>
      </html>
    `);
    iframeDoc.close();

    // Wait for iframe content and image to load
    const img = iframeDoc.getElementById('qr-image');
    const triggerPrint = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          console.error('Print error:', e);
          // Fallback: try window.print() which prints the whole page
          window.print();
        }
        // Clean up iframe after a delay (give time for print dialog)
        setTimeout(() => {
          if (printIframeRef.current && document.body.contains(printIframeRef.current)) {
            document.body.removeChild(printIframeRef.current);
            printIframeRef.current = null;
          }
        }, 1000);
      }, 300); // Longer delay for mobile rendering
    };

    if (img) {
      if (img.complete) {
        triggerPrint();
      } else {
        img.onload = triggerPrint;
        img.onerror = () => {
          alert('Failed to load QR code image for printing');
          if (printIframeRef.current && document.body.contains(printIframeRef.current)) {
            document.body.removeChild(printIframeRef.current);
            printIframeRef.current = null;
          }
        };
      }
    } else {
      triggerPrint();
    }
  };

  // Manage QR handlers
  const handleGenerateNew = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post(`/boxes/${boxId}/regenerate-uuid`);
      setSuccessMessage('New QR code generated successfully!');
      setManageStep('success');
      // Refresh QR code data
      await fetchQRCode();
      if (onQrUpdate) onQrUpdate(response.data);
    } catch (err) {
      console.error('Error generating new UUID:', err);
      setError(err.response?.data?.error || 'Failed to generate new QR code.');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async ({ uuid }) => {
    setScannedUuid(uuid);
    setError(null);
    await checkUuidStatus(uuid);
  };

  const checkUuidStatus = async (uuid) => {
    setLoading(true);
    try {
      const response = await api.get(`/boxes/uuid/${uuid}/check`);
      
      if (response.data.status === 'taken') {
        setError('This QR code is already assigned to another user\'s box.');
        setManageStep('scan');
      } else if (response.data.status === 'owned' && response.data.box?.id !== boxId) {
        setError(`This QR code is already assigned to your box "${response.data.box?.name}".`);
        setManageStep('scan');
      } else {
        setManageStep('confirm');
      }
    } catch (err) {
      console.error('Error checking UUID:', err);
      setError('Failed to check QR code status.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = async () => {
    const uuid = manualUuid.trim().toLowerCase();
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (!uuidPattern.test(uuid)) {
      setError('Invalid UUID format.');
      return;
    }
    
    setScannedUuid(uuid);
    await checkUuidStatus(uuid);
  };

  const handleAssign = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post(`/boxes/${boxId}/assign-uuid`, { uuid: scannedUuid });
      setSuccessMessage('QR code assigned successfully!');
      setManageStep('success');
      await fetchQRCode();
      if (onQrUpdate) onQrUpdate(response.data);
    } catch (err) {
      console.error('Error assigning UUID:', err);
      setError(err.response?.data?.error || 'Failed to assign QR code.');
    } finally {
      setLoading(false);
    }
  };

  const renderPrintTab = () => (
    <Box ref={printRef} sx={{ textAlign: 'center' }}>
      {!currentUuid && !qrCodeData ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No QR code assigned yet. Go to the "Manage QR" tab to generate or assign one.
        </Alert>
      ) : qrCodeData ? (
        <>
          <Box
            sx={{
              display: 'inline-block',
              p: 3,
              border: '2px dashed',
              borderColor: 'rgba(0,0,0,0.2)',
              borderRadius: 3,
              backgroundColor: '#fff'
            }}
          >
            <Box
              component="img"
              src={`data:image/png;base64,${qrCodeData.qr_code}`}
              alt="QR Code"
              sx={{ width: 200, height: 200, display: 'block', margin: '0 auto 16px' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.darkSlateBlue, wordBreak: 'break-word', maxWidth: 250 }}>
              {boxName || 'Unnamed Box'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: colors.mediumSlateBlue, mt: 1 }}>
              Scan to view contents
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(0,0,0,0.4)', mt: 1.5, fontSize: '0.7rem' }}>
              ClutterLogix
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 3, color: colors.mediumSlateBlue, maxWidth: 300, mx: 'auto' }}>
            Print this label and stick it on your box.
          </Typography>
        </>
      ) : null}
    </Box>
  );

  const renderManageTab = () => {
    if (loading && manageStep !== 'scan') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Processing...</Typography>
        </Box>
      );
    }

    switch (manageStep) {
      case 'choose':
        return (
          <Box>
            {currentUuid && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">Current QR code assigned.</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                  {currentUuid}
                </Typography>
              </Alert>
            )}
            
            <Paper
              sx={{
                p: 2,
                mb: 2,
                border: '2px solid',
                borderColor: colors.actionBlue,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: shadows.md }
              }}
              onClick={handleGenerateNew}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AutorenewIcon sx={{ fontSize: 36, color: colors.actionBlue }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Generate New QR Code</Typography>
                  <Typography variant="body2" color="text.secondary">Create a new QR code to print</Typography>
                </Box>
              </Box>
            </Paper>
            
            <Paper
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: shadows.md }
              }}
              onClick={() => setManageStep('scan')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QrCodeScannerIcon sx={{ fontSize: 36, color: colors.mediumSlateBlue }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Scan Existing QR Code</Typography>
                  <Typography variant="body2" color="text.secondary">Use a pre-printed QR label</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        );
      
      case 'scan':
        return (
          <Box>
            <Button variant="text" onClick={() => { setManageStep('choose'); setError(null); }} sx={{ mb: 2 }}>
              ← Back
            </Button>
            
            {!showManualEntry ? (
              <>
                <QRScanner onScan={handleScan} onError={(err) => setError(err.message)} />
                <Divider sx={{ my: 2 }}><Typography variant="body2" color="text.secondary">OR</Typography></Divider>
                <Button variant="outlined" fullWidth onClick={() => setShowManualEntry(true)}>Enter UUID Manually</Button>
              </>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  label="UUID from QR Label"
                  value={manualUuid}
                  onChange={(e) => setManualUuid(e.target.value)}
                  placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" onClick={() => { setShowManualEntry(false); setManualUuid(''); }}>Back to Scanner</Button>
                  <Button variant="contained" onClick={handleManualEntry} disabled={!manualUuid.trim()} sx={{ backgroundColor: colors.actionBlue }}>Check UUID</Button>
                </Box>
              </Box>
            )}
          </Box>
        );
      
      case 'confirm':
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <QrCodeScannerIcon sx={{ fontSize: 48, color: colors.actionBlue, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>Assign this QR Code?</Typography>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{scannedUuid}</Typography>
            </Paper>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => { setManageStep('scan'); setScannedUuid(null); }}>Scan Different</Button>
              <Button variant="contained" onClick={handleAssign} sx={{ backgroundColor: colors.actionBlue }}>Assign</Button>
            </Box>
          </Box>
        );
      
      case 'success':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>{successMessage}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You can now print the QR label from the "Print Label" tab.
            </Typography>
            <Button variant="contained" onClick={() => setActiveTab(0)} sx={{ backgroundColor: colors.actionBlue }}>Go to Print</Button>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: shadows.xl } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeIcon sx={{ color: colors.actionBlue }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.darkSlateBlue }}>
            QR Code
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ px: 3, borderBottom: '1px solid rgba(0,0,0,0.08)' }}
      >
        <Tab label="Print Label" icon={<PrintIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
        <Tab label="Manage QR" icon={<QrCodeScannerIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
      </Tabs>

      <DialogContent sx={{ py: 3, minHeight: 300 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {activeTab === 0 && (loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.actionBlue }} />
          </Box>
        ) : renderPrintTab())}
        
        {activeTab === 1 && renderManageTab()}
      </DialogContent>

      {activeTab === 0 && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button onClick={onClose} sx={{ color: colors.mediumSlateBlue }}>Close</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={loading || !qrCodeData}
            sx={{ backgroundColor: colors.actionBlue, borderRadius: 2, px: 3, '&:hover': { backgroundColor: colors.darkSlateBlue } }}
          >
            Print Label
          </Button>
        </DialogActions>
      )}
      
      {activeTab === 1 && manageStep !== 'success' && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button onClick={onClose} sx={{ color: colors.mediumSlateBlue }}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default QRLabelPrint;
